import React, { useEffect, useMemo, useRef, useState } from 'react';

import { FolderOpenOutlined, TranslationOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Tooltip,
  message,
} from 'antd';
import type { InputRef } from 'antd';
import { useTranslation } from 'react-i18next';

import { icons } from 'components/DynamicIcon/DynamicIcon';

import { ApiErrorData } from 'models/ApiResponse';
import { Menu } from 'models/Menu';
import { MenuNode } from 'models/MenuNode';

import {
  MenuService,
  UpdateMenuForCurrentCompanyRequestDto,
} from 'services/MenuService';

interface EditContainerModalProps {
  code: string;
  isOpen: boolean;
  handleModalCancel: () => void;
  handleNodeUpdated: (node: MenuNode) => void;
}

function EditContainerModal({
  code,
  isOpen,
  handleModalCancel,
  handleNodeUpdated,
}: EditContainerModalProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [menu, setMenu] = useState<Menu>();
  const [loading, setLoading] = useState(false);
  const [useLocalizedTitles, setUseLocalizedTitles] = useState(false);
  const titleInputRef = useRef<InputRef>(null);

  const formattedIcons = useMemo(
    () =>
      Object.entries(icons)
        .map(([key, value]) => ({
          value: key,
          label: key,
          icon: value,
        }))
        .map((item) => ({
          value: item.value,
          label: (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {item.icon && <span style={{ marginRight: 8 }}>{item.icon}</span>}
              {item.label}
            </div>
          ),
        })),
    []
  );

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      fetchData();
    } else {
      setMenu(undefined);
      setUseLocalizedTitles(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!menu) return;

    // Prellenar campos con los datos del menú
    form.setFieldsValue({
      title: menu.title || '',
      localizedTitleEs: (menu.localizedTitles?.es || '') ?? '',
      localizedTitleEn: (menu.localizedTitles?.en || '') ?? '',
    });

    const hasLocalized = !!(
      (menu.localizedTitles?.es && menu.localizedTitles.es.trim()) ||
      (menu.localizedTitles?.en && menu.localizedTitles.en.trim())
    );
    setUseLocalizedTitles(hasLocalized);

    // Preseleccionar el icono actual si existe
    if (menu.icon) {
      const currentIcon = formattedIcons.find((i) => i.value === menu.icon);
      if (currentIcon) {
        form.setFieldValue('icon', currentIcon);
      }
    }
    // Enfocar y seleccionar el input de título una vez cargados los datos
    setTimeout(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select?.();
    }, 0);
  }, [menu, form, formattedIcons]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await MenuService.findByCode(code);

      if (response.success) {
        setMenu(response.data as Menu);
      } else {
        const errorData = response.data as ApiErrorData;
        message.error(
          errorData.message ||
            t(
              'scenes.new_report_and_menus.components.edit_container_modal.errors.fetch_failed'
            ),
          3.5
        );
        handleModalCancel();
      }
    } catch (err: any) {
      message.error(
        err?.message ||
          t(
            'scenes.new_report_and_menus.components.edit_container_modal.errors.fetch_unexpected'
          ),
        3.5
      );
      handleModalCancel();
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values: any) => {
    if (menu) {
      setLoading(true);
      try {
        const payload: UpdateMenuForCurrentCompanyRequestDto = {
          title: (values.title || '').trim(),
          localizedTitles: useLocalizedTitles
            ? {
                es: (values.localizedTitleEs || '').trim() || null,
                en: (values.localizedTitleEn || '').trim() || null,
              }
            : null,
          icon: values.icon ? (values.icon.value as string) : null,
          redirectLink: menu.redirectLink,
          menuType: menu.menuTypeId,
          additionalConfig: menu.additionalConfig,
        };

        const response = await MenuService.updateForCurrentCompany(
          code,
          payload
        );

        if (response.success) {
          const { updatedNode } = response.data as { updatedNode: MenuNode };
          message.success(
            t(
              'scenes.new_report_and_menus.components.edit_container_modal.success.updated'
            ),
            3.5
          );
          handleNodeUpdated(updatedNode);
          handleModalCancel();
        } else {
          const err = response.data as any;
          message.error(
            err?.message ||
              t(
                'scenes.new_report_and_menus.components.edit_container_modal.errors.update_failed'
              ),
            3.5
          );
        }
      } catch (err: any) {
        message.error(
          err?.message ||
            t(
              'scenes.new_report_and_menus.components.edit_container_modal.errors.update_unexpected'
            ),
          3.5
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal
      title={t(
        'scenes.new_report_and_menus.components.edit_container_modal.title'
      )}
      open={isOpen}
      onCancel={handleModalCancel}
      maskClosable={false}
      destroyOnClose
      footer={null}
      width={480}
    >
      <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              label={t(
                'scenes.new_report_and_menus.components.new_container_modal.labels.parent_container'
              )}
            >
              <Input
                addonBefore={<FolderOpenOutlined />}
                type="text"
                placeholder={t(
                  'scenes.new_report_and_menus.components.new_container_modal.placeholders.parent_container'
                )}
                readOnly
                defaultValue={
                  menu?.parentMenuCode
                    ? menu.parentMenuCode
                    : `${window.location.origin}`
                }
                disabled={loading}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              name="title"
              label={t(
                'scenes.new_report_and_menus.components.new_container_modal.labels.title'
              )}
              extra={t(
                'scenes.new_report_and_menus.components.new_container_modal.hints.title_fallback'
              )}
              rules={[
                {
                  required: true,
                  message: t(
                    'scenes.new_report_and_menus.components.new_container_modal.validations.title.required'
                  ),
                },
              ]}
            >
              <Input
                ref={titleInputRef}
                type="text"
                disabled={loading}
                placeholder={t(
                  'scenes.new_report_and_menus.components.new_container_modal.placeholders.title'
                )}
                maxLength={80}
                suffix={
                  <Tooltip
                    title={t(
                      'scenes.new_report_and_menus.components.new_container_modal.tooltips.add_translations'
                    )}
                  >
                    <TranslationOutlined
                      onClick={() => {
                        const next = !useLocalizedTitles;
                        setUseLocalizedTitles(next);
                        if (next) {
                          const base = (
                            form.getFieldValue('title') || ''
                          ).trim();
                          form.setFieldsValue({
                            localizedTitleEs: base,
                            localizedTitleEn: base,
                          });
                        } else {
                          form.setFieldsValue({
                            localizedTitleEs: '',
                            localizedTitleEn: '',
                          });
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        color: useLocalizedTitles ? '#1677ff' : undefined,
                      }}
                    />
                  </Tooltip>
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {useLocalizedTitles && (
          <div
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
              }}
            >
              <TranslationOutlined />
              <span>
                {t(
                  'scenes.new_report_and_menus.components.new_container_modal.sections.translations'
                )}
              </span>
            </div>
            <Row gutter={12}>
              <Col xs={24}>
                <Form.Item
                  name="localizedTitleEn"
                  label={t(
                    'scenes.new_report_and_menus.components.new_container_modal.labels.title_en'
                  )}
                >
                  <Input
                    type="text"
                    disabled={loading}
                    placeholder={t(
                      'scenes.new_report_and_menus.components.new_container_modal.placeholders.title_en'
                    )}
                    maxLength={80}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col xs={24}>
                <Form.Item
                  name="localizedTitleEs"
                  label={t(
                    'scenes.new_report_and_menus.components.new_container_modal.labels.title_es'
                  )}
                >
                  <Input
                    type="text"
                    disabled={loading}
                    placeholder={t(
                      'scenes.new_report_and_menus.components.new_container_modal.placeholders.title_es'
                    )}
                    maxLength={80}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
        )}

        {/* Input de icono: opcional y siempre visible */}
        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              name="icon"
              label={t(
                'scenes.new_report_and_menus.components.new_container_modal.labels.icon'
              )}
            >
              <Select
                placeholder={t(
                  'scenes.new_report_and_menus.components.new_container_modal.placeholders.select_option'
                )}
                allowClear
                labelInValue
                options={formattedIcons}
                showSearch
                disabled={loading}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24}>
            <Button
              onClick={() => {
                form.submit();
              }}
              loading={loading}
              type="primary"
              block
            >
              {t(
                'scenes.new_report_and_menus.components.new_container_modal.buttons.save_changes'
              )}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default EditContainerModal;
